import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Pencil, Repeat, Trash2 } from 'lucide-react';
import {
  useSubscription,
  useSubscriptionCategories,
  useUpdateSubscription,
  useDeleteSubscription,
} from '../hooks/useSubscriptions';
import { useSubscriptionStats } from '../hooks/useEntityStats';
import {
  SUBSCRIPTION_FREQUENCY_LABELS,
  subscriptionCategoryLabel,
  type SubscriptionFrequency,
  type SubscriptionInput,
} from '../lib/subscriptionsApi';
import { FadeIn } from '../components/ui/Skeleton';
import { SubscriptionFormModal } from '../components/subscriptions/SubscriptionFormModal';
import { FinancialStatsSection } from '../components/financials/FinancialStatsSection';

const TABS = ['overview', 'financiar'] as const;
type Tab = (typeof TABS)[number];

const TAB_LABELS: Record<Tab, string> = {
  overview: 'Prezentare generală',
  financiar: 'Financiar',
};

/**
 * Next charge date for a monthly subscription. Mirrors the cash-flow projection
 * (backend financials.cashflow.ts): the day is clamped to the length of the
 * month, and a day already past in the current month rolls over to the next.
 */
function nextPaymentDate(paymentDay: number): Date {
  const today = new Date();
  const clamp = (y: number, m: number) => Math.min(paymentDay, new Date(y, m + 1, 0).getDate());
  const y = today.getFullYear();
  const m = today.getMonth();
  if (clamp(y, m) >= today.getDate()) return new Date(y, m, clamp(y, m));
  return new Date(y, m + 1, clamp(y, m + 1));
}

export function SubscriptionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: subscription, isLoading, isError } = useSubscription(id);
  const { data: categoryDefs } = useSubscriptionCategories();
  const statsQuery = useSubscriptionStats(id);
  const updateMutation = useUpdateSubscription(id ?? '');
  const deleteMutation = useDeleteSubscription();
  const [showEdit, setShowEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const initialTab = (window.location.hash.replace('#', '') as Tab) || 'overview';
  const [tab, setTab] = useState<Tab>(TABS.includes(initialTab) ? initialTab : 'overview');

  function selectTab(next: Tab) {
    setTab(next);
    window.location.hash = next;
  }

  async function handleUpdate(input: SubscriptionInput) {
    await updateMutation.mutateAsync(input);
    setShowEdit(false);
  }

  async function handleDelete() {
    if (!id) return;
    await deleteMutation.mutateAsync(id);
    navigate('/subscriptions');
  }

  if (isLoading) {
    return <div className="p-8 text-gray-500">Se încarcă…</div>;
  }
  if (isError || !subscription) {
    return (
      <div className="p-8">
        <p className="text-red-600">Nu s-a putut încărca abonamentul.</p>
        <Link to="/subscriptions" className="mt-2 inline-block text-brand-700 hover:underline">
          Înapoi la abonamente
        </Link>
      </div>
    );
  }

  const isMonthly = subscription.frequency === 'monthly';
  // The projection falls back to the 1st when no day is set, so show the same
  // effective day here rather than an empty cell.
  const effectivePaymentDay = subscription.paymentDay ?? 1;
  const currency = subscription.expectedCurrency ?? 'RON';

  const rows: { label: string; value: React.ReactNode }[] = [
    {
      label: 'Categorie',
      value: subscriptionCategoryLabel(subscription.category, categoryDefs),
    },
    {
      label: 'Sumă estimată',
      value:
        subscription.expectedAmount != null
          ? `${subscription.expectedAmount.toLocaleString('ro-RO')} ${currency}`
          : '—',
    },
    {
      label: 'Frecvență',
      value:
        SUBSCRIPTION_FREQUENCY_LABELS[subscription.frequency as SubscriptionFrequency] ??
        subscription.frequency,
    },
    {
      label: 'Ziua plății',
      value: isMonthly ? (
        <>
          În fiecare lună pe data de {effectivePaymentDay}
          {subscription.paymentDay === null && (
            <span className="ml-1 text-gray-500">(implicit)</span>
          )}
        </>
      ) : (
        'Nu se aplică (doar la abonamentele lunare)'
      ),
    },
    ...(isMonthly
      ? [
          {
            label: 'Următoarea plată',
            value: `${nextPaymentDate(effectivePaymentDay).toLocaleDateString('ro-RO', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}${
              subscription.expectedAmount != null
                ? ` · ${subscription.expectedAmount.toLocaleString('ro-RO')} ${currency}`
                : ''
            }`,
          },
        ]
      : []),
    {
      label: 'Furnizor',
      value: subscription.supplier ? (
        <Link
          to={`/suppliers/${subscription.supplier.id}`}
          className="text-brand-700 hover:underline"
        >
          {subscription.supplier.name}
        </Link>
      ) : (
        '—'
      ),
    },
    { label: 'Firmă', value: subscription.company?.name ?? '—' },
    {
      label: 'Pattern-uri comerciant',
      value:
        subscription.merchantPatterns.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {subscription.merchantPatterns.map((p) => (
              <span
                key={p}
                className="inline-block rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-600"
              >
                {p}
              </span>
            ))}
          </div>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <FadeIn>
      <div className="w-full p-4 sm:p-6 lg:p-8">
        <Link
          to="/subscriptions"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft size={16} />
          Abonamente
        </Link>

        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <Repeat className="text-brand-600" size={24} />
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">{subscription.name}</h1>
              <span
                className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                  subscription.isActive
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {subscription.isActive ? 'Activ' : 'Inactiv'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowEdit(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Pencil size={16} />
              Editează
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
            >
              <Trash2 size={16} />
              Șterge
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 border-b border-gray-200">
          <nav className="-mb-px flex gap-6 overflow-x-auto">
            {TABS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => selectTab(t)}
                className={`whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium ${
                  tab === t
                    ? 'border-brand-600 text-brand-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {TAB_LABELS[t]}
              </button>
            ))}
          </nav>
        </div>

        <div className="mt-6">
          <FadeIn>
            {tab === 'overview' && (
              <div className="max-w-2xl">
                <div className="overflow-hidden rounded-lg border border-gray-200">
                  <dl className="divide-y divide-gray-100">
                    {rows.map((r) => (
                      <div key={r.label} className="grid grid-cols-3 gap-2 px-4 py-3">
                        <dt className="text-sm font-medium text-gray-500">{r.label}</dt>
                        <dd className="col-span-2 text-sm text-gray-900">{r.value}</dd>
                      </div>
                    ))}
                    {subscription.notes && (
                      <div className="px-4 py-3">
                        <dt className="mb-1 text-sm font-medium text-gray-500">Notițe</dt>
                        <dd className="whitespace-pre-wrap text-sm text-gray-900">
                          {subscription.notes}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
                {isMonthly && subscription.expectedAmount != null && (
                  <p className="mt-3 text-sm text-gray-500">
                    Această sumă apare automat în{' '}
                    <Link to="/financials/cashflow" className="text-brand-700 hover:underline">
                      proiecția de cash flow
                    </Link>{' '}
                    în fiecare lună, la data de {effectivePaymentDay}.
                  </p>
                )}
                {isMonthly && subscription.expectedAmount == null && (
                  <p className="mt-3 text-sm text-amber-700">
                    Fără o sumă estimată, abonamentul nu intră în{' '}
                    <Link to="/financials/cashflow" className="underline">
                      proiecția de cash flow
                    </Link>
                    .
                  </p>
                )}
              </div>
            )}
            {tab === 'financiar' && (
              <FinancialStatsSection
                stats={statsQuery.data}
                isLoading={statsQuery.isLoading}
                direction="out"
              />
            )}
          </FadeIn>
        </div>

        {showEdit && (
          <SubscriptionFormModal
            title="Editează abonamentul"
            initial={subscription}
            onClose={() => setShowEdit(false)}
            onSubmit={handleUpdate}
            submitting={updateMutation.isPending}
          />
        )}

        {confirmDelete && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setConfirmDelete(false);
            }}
          >
            <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
              <h2 className="text-lg font-semibold text-gray-900">Ștergi abonamentul?</h2>
              <p className="mt-2 text-sm text-gray-600">
                „{subscription.name}” va fi dezactivat și nu va mai fi folosit pentru potrivirea
                automată. Îl poți reactiva ulterior prin editare.
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Anulează
                </button>
                <button
                  type="button"
                  onClick={() => void handleDelete()}
                  disabled={deleteMutation.isPending}
                  className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  Șterge
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </FadeIn>
  );
}
